import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, RefreshCw, AlertTriangle, Loader2, Undo2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin editor for the media-consent wording + version.
 *
 * Anything typed here is served live by `GET /api/media-consent-config` and
 * rendered on the onboarding page + profile editor for every user. Two save
 * modes:
 *
 *   • "Save wording only" — replaces the copy but keeps the version, so
 *     already-answered users stay answered. Useful for typo fixes.
 *   • "Save & re-prompt everyone" — bumps the version, which invalidates
 *     every user's stored consent + coach-ack on their next /auth/me call.
 *     Use this when the scope of consent has actually changed (e.g. widening
 *     to include marketing usage).
 */
export default function AdminMediaConsent() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    player_question: "",
    player_description: "",
    coach_ack_text: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // "wording" | "version" | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media-consent-config");
      setCfg(data);
      setForm({
        player_question: data.player_question || "",
        player_description: data.player_description || "",
        coach_ack_text: data.coach_ack_text || "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = cfg && (
    form.player_question !== cfg.player_question
    || form.player_description !== cfg.player_description
    || form.coach_ack_text !== cfg.coach_ack_text
  );

  const persist = async (bump) => {
    if (!dirty && !bump) {
      toast("Nothing to save.", { description: "Edit some text or use 'Re-prompt everyone' to bump the version." });
      return;
    }
    setSaving(bump ? "version" : "wording");
    try {
      const payload = {
        player_question: form.player_question,
        player_description: form.player_description,
        coach_ack_text: form.coach_ack_text,
        bump_version: !!bump,
      };
      const { data } = await api.post("/admin/media-consent-config", payload);
      setCfg(data.config);
      setForm({
        player_question: data.config.player_question,
        player_description: data.config.player_description,
        coach_ack_text: data.config.coach_ack_text,
      });
      toast.success(bump
        ? `Version bumped to ${data.config.version}. Every user will be re-prompted on their next login.`
        : "Wording saved. No re-prompt.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const reset = () => {
    if (!cfg) return;
    setForm({
      player_question: cfg.player_question,
      player_description: cfg.player_description,
      coach_ack_text: cfg.coach_ack_text,
    });
  };

  const confirmBump = () => {
    if (!window.confirm(
      "Bump the consent version?\n\nEvery user (players, coaches, admins) will be forced to answer the consent question again on their NEXT LOGIN. Do this only when the scope of what you're asking has actually changed.\n\nContinue?"
    )) return;
    persist(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-media-consent-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider">
        <ArrowLeft className="w-3.5 h-3.5" /> Admin
      </Link>

      <header className="fade-up">
        <div className="label-mono mb-2">SETTINGS · MEDIA CONSENT</div>
        <h1 className="h-display text-4xl sm:text-5xl tracking-tighter">Consent wording</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
          Edit the text every player and coach sees on the media-consent screen. Bump the version
          only when the meaning of the question changes — bumping forces every user to answer again.
        </p>
      </header>

      {loading ? (
        <div className="card-surface p-6 text-[#A3A3A3] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            <FormField
              label="Player consent question"
              hint="Shown as the main heading on the onboarding page + registration checkbox."
              value={form.player_question}
              onChange={(v) => setForm((f) => ({ ...f, player_question: v }))}
              testid="mc-cfg-player-question"
              rows={2}
            />
            <FormField
              label="Player description"
              hint="Explanatory paragraph under the yes/no buttons. Reassure them how clips are used."
              value={form.player_description}
              onChange={(v) => setForm((f) => ({ ...f, player_description: v }))}
              testid="mc-cfg-player-description"
              rows={5}
            />
            <FormField
              label="Coach acknowledgement"
              hint="Data-controller ack — coaches must tick to confirm they've read it."
              value={form.coach_ack_text}
              onChange={(v) => setForm((f) => ({ ...f, coach_ack_text: v }))}
              testid="mc-cfg-coach-ack"
              rows={5}
            />
          </div>

          <section className="card-surface p-5 border-l-4 border-[#CCFF00]" data-testid="mc-cfg-version-card">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">CURRENT VERSION</div>
            </div>
            <div className="h-display text-3xl tracking-tighter" data-testid="mc-cfg-version">{cfg.version}</div>
            {cfg.updated_at && (
              <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1">
                Last saved: {new Date(cfg.updated_at).toLocaleString()}
              </div>
            )}
          </section>

          <section className="card-surface p-5 border border-dashed border-[#FF4500]/50" data-testid="mc-cfg-danger">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#FF4500] mt-0.5" />
              <div>
                <div className="label-mono" style={{ color: "#FF4500" }}>RE-PROMPT ALL USERS</div>
                <p className="text-xs text-[#A3A3A3] mt-1 leading-relaxed">
                  Bumps the version. Every player, coach and admin will be forced to answer again on their next login. Their previous answer is preserved as history.
                </p>
              </div>
            </div>
            <button
              onClick={confirmBump}
              disabled={saving !== null}
              className="px-4 py-2 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="mc-cfg-bump-version"
            >
              {saving === "version" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Save wording &amp; re-prompt everyone
            </button>
          </section>

          <div className="flex items-center justify-end gap-3 pt-2">
            {dirty && (
              <button
                onClick={reset}
                className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-2"
                data-testid="mc-cfg-reset"
              >
                <Undo2 className="w-3.5 h-3.5" /> Discard changes
              </button>
            )}
            <button
              onClick={() => persist(false)}
              disabled={!dirty || saving !== null}
              className="btn-volt px-6 py-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="mc-cfg-save-wording"
            >
              {saving === "wording" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Save wording only
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FormField({ label, hint, value, onChange, testid, rows = 3 }) {
  return (
    <label className="block">
      <div className="label-mono mb-1">{label}</div>
      {hint && <div className="text-[10px] uppercase tracking-wider text-[#525252] mb-2">{hint}</div>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#CCFF00] outline-none resize-y"
      />
    </label>
  );
}
